import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import Button, { RemoveButton } from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';
import { RootState } from '@/utils/store';

const VariationWrap = styled(Larger)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: 24px;
  grid-column-gap: 24px;
  margin: 0 -32px;
  padding: 0 32px;
  padding: 24px 32px;
  background-color: ${({ theme }) => theme.colors.grayscaleSuperLight};

  &:first-child {
    margin-top: -24px;
  }
`;

const VariationHeading = styled(Larger)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TimeTitle = styled(Text)`
  margin: 8px 0 -8px;
`;

const NumberBadge = styled(Text)`
  display: flex;
  height: 24px;
  width: 48px;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.purpleLighter};
  border-radius: 100px;
`;

const AddButtonHolder = styled.div`
  text-align: right;
  margin-bottom: 8px;
  svg {
    margin-right: 8px;
  }
  button {
    width: 100%;
  }
`;

interface FormDataT {
  team_id: number | null;
  csv: File | null;
  links: Array<{
    url: string;
    start_time: number;
    end_time: number;
  }>;
}

const DEFAULT_VARIATION = {
  url: '',
  start_time: 0,
  end_time: 600,
};

interface Props {
  onClose: VoidFunction;
}

const UploadLinkForm: FC<Props> = ({ onClose }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const teams = useSelector((state: RootState) => state.teams);

  const {
    handleSubmit,
    clearErrors,
    control,
    watch,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      team_id: null,
      csv: null,
      links: [
        {
          ...DEFAULT_VARIATION,
        },
      ],
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'links',
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(async ({ csv, ...data }) => {
      try {
        await api.post(`/v1/eytv-links`, data);

        await queryClient.invalidateQueries(['eytv_links']);

        onClose();

        alerts.success(
          'Videos is being processed',
          'This might take a few minutes',
        );
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  const downloadSampleCsv = () => {
    // Define the CSV content
    const rows = [
      ['link'],
      ['https://www.youtube.com/watch?v=LYEo5obY8Uc'],
      ['https://www.youtube.com/watch?v=-h1h0AT8vE4'],
    ];

    // Convert array of arrays into CSV string
    const csvContent = rows.map((e) => e.join(',')).join('\n');

    // Create a Blob from the CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create a temporary link element
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample.csv');
    document.body.appendChild(link);

    // Trigger the download
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const csv = watch('csv');

  useEffect(() => {
    if (!csv) {
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        const fileReader = event.target;
        if (fileReader && typeof fileReader.result === 'string') {
          const text = fileReader.result;

          // Split into rows by newline
          const lines = text.split(/\r?\n/).filter(Boolean);

          // Convert into arrays (basic split by comma)
          const parsed = lines.map((line) => line.split(','));

          // Remove the first row (header)
          const withoutHeader = parsed.slice(1);

          const rows = withoutHeader.map((url) => ({
            ...DEFAULT_VARIATION,
            url: url[0],
          }));

          replace(rows);
        }
      };

      reader.readAsText(csv);
    } catch {
      alerts.error('Failed to read CSV file');
    }
    // eslint-disable-next-line
  }, [csv, replace]);

  return (
    <Modal>
      <SidePanel heading="Upload link" onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT, number>
                label="Team"
                control={control}
                name={`team_id`}
                type="select"
                options={Object.values(teams).map((t) => ({
                  label: t.name,
                  value: t.id,
                }))}
              />
              <FormField<FormDataT>
                id="etyvLinksCsvInput"
                label="Prefill links from CSV"
                control={control}
                notes="Supports: CSV file"
                name={`csv`}
                accept=".csv"
                type="file"
              />
              <Larger>
                <Button
                  $type="secondary"
                  $small
                  type="button"
                  onClick={downloadSampleCsv}
                >
                  Download sample CSV file
                </Button>
              </Larger>
              {fields.map((item, index) => (
                <VariationWrap key={item.id}>
                  <VariationHeading>
                    <NumberBadge $type="smallBold" $color="white">
                      Nr. {index + 1}
                    </NumberBadge>
                    <RemoveButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      Remove
                    </RemoveButton>
                  </VariationHeading>
                  <FormField<FormDataT, number>
                    label="Youtube link"
                    control={control}
                    name={`links.${index}.url`}
                    placeholder="https://www.youtube.com/watch?v=xxx"
                  />
                  <TimeTitle $type="caption">Video trim</TimeTitle>
                  <FormField<FormDataT, number>
                    label="Start time"
                    control={control}
                    type="duration"
                    name={`links.${index}.start_time`}
                  />
                  <FormField<FormDataT, number>
                    label="End time"
                    control={control}
                    type="duration"
                    name={`links.${index}.end_time`}
                  />
                </VariationWrap>
              ))}
              <Larger>
                <AddButtonHolder>
                  <Button
                    type="button"
                    $type="colorized"
                    onClick={() => append({ ...DEFAULT_VARIATION })}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 2V14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 8H14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Add additional link
                  </Button>
                </AddButtonHolder>
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Add
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default UploadLinkForm;
